import { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { useAuth } from '../hooks/useAuth';
import { updateProfile, joinSuperteam, leaveSuperteam, joinCountry, leaveCountry, getAchievements, createAchievement, updateAchievement, deleteAchievement } from '../utils/api';
import CountrySelector from './CountrySelector';
import type { Superteam, Country, Achievement, AchievementType } from '../types';

interface ProfileFormProps {
  isOpen: boolean;
  onClose: () => void;
  superteams: Superteam[];
  countries: Country[];
}

export default function ProfileForm({ isOpen, onClose, superteams, countries }: ProfileFormProps) {
  const { user, updateUser } = useAuth();
  const [isMobile, setIsMobile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'achievements'>('profile');

  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [skills, setSkills] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState('');
  const [selectedSuperteam, setSelectedSuperteam] = useState<string>('');
  const [selectedCountryCode, setSelectedCountryCode] = useState<string | null>(null);
  const [github, setGithub] = useState('');
  const [website, setWebsite] = useState('');
  const [discord, setDiscord] = useState('');

  // Achievements state
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loadingAchievements, setLoadingAchievements] = useState(false);
  const [achievementsFetched, setAchievementsFetched] = useState(false);
  const [showAchievementForm, setShowAchievementForm] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState<Achievement | null>(null);
  const [achievementForm, setAchievementForm] = useState({
    type: 'hackathon_winner' as AchievementType,
    title: '',
    description: '',
    date: '',
    issuer: '',
    proofUrl: '',
    superteamId: '',
  });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (user) {
      setName(user.name || user.xDisplayName || '');
      setBio(user.bio || '');
      setSkills(user.skills || []);
      setSelectedSuperteam(user.superteamId || '');
      setSelectedCountryCode(user.countryCode || null);
      setGithub(user.links?.github || '');
      setWebsite(user.links?.website || '');
      setDiscord(user.links?.discord || '');
    }
  }, [user]);

  // Fetch achievements when achievements tab is first opened
  useEffect(() => {
    if (activeTab === 'achievements' && user && !loadingAchievements && !achievementsFetched) {
      setLoadingAchievements(true);
      getAchievements(user.id)
        .then(response => {
          if (response.success && response.data) {
            setAchievements(response.data.achievements);
            setAchievementsFetched(true);
          }
        })
        .catch(err => {
          console.error('Failed to fetch achievements:', err);
          setError('Failed to load achievements');
        })
        .finally(() => setLoadingAchievements(false));
    }
  }, [activeTab, user?.id, loadingAchievements, achievementsFetched]);

  // Reset fetched flag when tab changes away from achievements
  useEffect(() => {
    if (activeTab !== 'achievements') {
      setAchievementsFetched(false);
    }
  }, [activeTab]);

  const handleAddSkill = () => {
    const trimmed = skillInput.trim();
    if (trimmed && !skills.includes(trimmed) && skills.length < 10) {
      setSkills([...skills, trimmed]);
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skill: string) => {
    setSkills(skills.filter(s => s !== skill));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSkill();
    }
  };

  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const profileResponse = await updateProfile({
        name: name || undefined,
        bio: bio || undefined,
        skills: skills.length > 0 ? skills : undefined,
        links: { github: github || undefined, website: website || undefined, discord: discord || undefined },
      });

      if (profileResponse.success && profileResponse.data?.user) {
        updateUser(profileResponse.data.user);
      }

      // Handle superteam changes
      if (selectedSuperteam !== user.superteamId) {
        if (selectedSuperteam) {
          const joinResponse = await joinSuperteam(selectedSuperteam);
          if (joinResponse.success && joinResponse.data?.user) updateUser(joinResponse.data.user);
        } else if (user.superteamId) {
          const leaveResponse = await leaveSuperteam();
          if (leaveResponse.success && leaveResponse.data?.user) updateUser(leaveResponse.data.user);
        }
      }

      // Handle country changes
      if (selectedCountryCode !== user.countryCode) {
        if (selectedCountryCode) {
          const joinResponse = await joinCountry(selectedCountryCode);
          if (joinResponse.success && joinResponse.data?.user) updateUser(joinResponse.data.user);
        } else if (user.countryCode) {
          const leaveResponse = await leaveCountry();
          if (leaveResponse.success && leaveResponse.data?.user) updateUser(leaveResponse.data.user);
        }
      }

      setSuccess(true);
      setTimeout(() => { setSuccess(false); onClose(); }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAchievement = async () => {
    if (!user) {
      setError('You must be logged in to save achievements');
      return;
    }
    
    const trimmedTitle = achievementForm.title?.trim();
    if (!trimmedTitle || trimmedTitle.length === 0) {
      setError('Title is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (editingAchievement) {
        const response = await updateAchievement(editingAchievement.id, achievementForm);
        if (response.success) {
          // Optimistically update the list
          const updatedAchievement = response.data!.achievement;
          setAchievements(prev => prev.map(a => a.id === editingAchievement.id ? updatedAchievement : a));
          setEditingAchievement(null);
          setShowAchievementForm(false);
          setAchievementForm({ type: 'hackathon_winner', title: '', description: '', date: '', issuer: '', proofUrl: '', superteamId: '' });
          // Don't refetch - we already have the updated achievement
        } else {
          setError(response.error || 'Failed to update achievement');
        }
      } else {
        const response = await createAchievement(achievementForm);
        if (response.success) {
          // Optimistically update the list
          const newAchievement = response.data!.achievement;
          setAchievements(prev => [...prev, newAchievement]);
          setShowAchievementForm(false);
          setAchievementForm({ type: 'hackathon_winner', title: '', description: '', date: '', issuer: '', proofUrl: '', superteamId: '' });
          // Don't refetch - we already have the new achievement
        } else {
          setError(response.error || 'Failed to create achievement');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save achievement');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAchievement = async (id: string) => {
    if (!confirm('Are you sure you want to delete this achievement?')) return;

    setSaving(true);
    setError(null);

    try {
      const response = await deleteAchievement(id);
      if (response.success) {
        // Optimistically update the list
        setAchievements(prev => prev.filter(a => a.id !== id));
        // Don't refetch - we already removed it
      } else {
        setError(response.error || 'Failed to delete achievement');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete achievement');
    } finally {
      setSaving(false);
    }
  };

  const handleEditAchievement = (achievement: Achievement) => {
    setEditingAchievement(achievement);
    setAchievementForm({
      type: achievement.type,
      title: achievement.title,
      description: achievement.description || '',
      date: achievement.date || '',
      issuer: achievement.issuer || '',
      proofUrl: achievement.proofUrl || '',
      superteamId: achievement.superteamId || '',
    });
    setShowAchievementForm(true);
  };

  if (!user) return null;

  const backdropStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    zIndex: isMobile ? 200 : 50,
    transition: 'opacity 0.3s',
    opacity: isOpen ? 1 : 0,
    pointerEvents: isOpen ? 'auto' : 'none',
  };

  const panelStyle: CSSProperties = {
    position: 'fixed',
    zIndex: isMobile ? 201 : 50,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'rgba(10, 10, 20, 0.95)',
    backdropFilter: 'blur(30px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
    ...(isMobile ? {
      bottom: 0, left: 0, right: 0,
      borderRadius: '24px 24px 0 0',
      maxHeight: '90vh',
      transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
    } : {
      top: '80px', right: '24px', bottom: '24px',
      width: '420px',
      borderRadius: '20px',
      transform: isOpen ? 'translateX(0)' : 'translateX(calc(100% + 40px))',
    }),
  };

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '12px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: 'white',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  const labelStyle: CSSProperties = {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    color: 'rgba(255,255,255,0.7)',
    marginBottom: '8px',
  };

  return (
    <>
      <div style={backdropStyle} onClick={onClose} />

      <div style={panelStyle}>
        {/* Header */}
        <div style={{ flexShrink: 0, padding: '24px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          {isMobile && <div style={{ width: '48px', height: '4px', background: 'rgba(255,255,255,0.3)', borderRadius: '2px', margin: '0 auto 16px' }} />}
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {user.xAvatarUrl ? (
                <img src={user.xAvatarUrl} alt={user.xDisplayName} style={{ width: '56px', height: '56px', borderRadius: '16px', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'linear-gradient(135deg, #14F195, #9945FF)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '20px', fontWeight: 700 }}>
                  {(name || user.xDisplayName).charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'white' }}>Edit Profile</h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>@{user.xUsername}</p>
              </div>
            </div>
            
            <button onClick={onClose} style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              ×
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '8px', background: 'rgba(255,255,255,0.03)', padding: '4px', borderRadius: '12px' }}>
            <button 
              onClick={() => setActiveTab('profile')} 
              style={{ 
                flex: 1, 
                padding: '8px 16px', 
                borderRadius: '8px', 
                background: activeTab === 'profile' ? 'rgba(255,255,255,0.1)' : 'transparent',
                border: 'none',
                color: activeTab === 'profile' ? 'white' : 'rgba(255,255,255,0.6)',
                fontSize: '14px',
                fontWeight: activeTab === 'profile' ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              Profile
            </button>
            <button 
              onClick={() => setActiveTab('achievements')} 
              style={{ 
                flex: 1, 
                padding: '8px 16px', 
                borderRadius: '8px', 
                background: activeTab === 'achievements' ? 'rgba(255,255,255,0.1)' : 'transparent',
                border: 'none',
                color: activeTab === 'achievements' ? 'white' : 'rgba(255,255,255,0.6)',
                fontSize: '14px',
                fontWeight: activeTab === 'achievements' ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              Achievements
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {error && <div style={{ padding: '12px', borderRadius: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', fontSize: '14px', marginBottom: '24px' }}>{error}</div>}
          {success && <div style={{ padding: '12px', borderRadius: '12px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', color: '#4ade80', fontSize: '14px', marginBottom: '24px' }}>Profile saved successfully!</div>}

          {activeTab === 'profile' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Name */}
            <div>
              <label style={labelStyle}>Display Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" style={inputStyle} />
            </div>

            {/* Bio */}
            <div>
              <label style={labelStyle}>Bio</label>
              <textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell us about yourself..." rows={3} style={{ ...inputStyle, resize: 'none' }} />
            </div>

            {/* Superteam */}
            <div>
              <label style={labelStyle}>Your Superteam</label>
              <select value={selectedSuperteam} onChange={(e) => setSelectedSuperteam(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">Select your Superteam</option>
                {superteams.map((team) => (
                  <option key={team.id} value={team.id}>{team.name} ({team.country})</option>
                ))}
              </select>
            </div>

            {/* Country Representation */}
            <div>
              <label style={labelStyle}>
                Represent Your Country
                <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.4)', marginLeft: '8px', fontSize: '12px' }}>
                  (separate from Superteam)
                </span>
              </label>
              <CountrySelector
                countries={countries}
                selectedCode={selectedCountryCode}
                onChange={setSelectedCountryCode}
              />
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginTop: '8px' }}>
                Show solidarity for countries without Superteams yet! 🌍
              </p>
            </div>

            {/* Skills */}
            <div>
              <label style={labelStyle}>Skills</label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input type="text" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Add a skill..." style={{ ...inputStyle, flex: 1 }} />
                <button onClick={handleAddSkill} disabled={!skillInput.trim() || skills.length >= 10} style={{ padding: '12px 16px', borderRadius: '12px', background: 'rgba(20,241,149,0.2)', color: '#14F195', fontWeight: 500, border: 'none', cursor: 'pointer', opacity: !skillInput.trim() || skills.length >= 10 ? 0.5 : 1 }}>
                  Add
                </button>
              </div>
              {skills.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {skills.map((skill) => (
                    <span key={skill} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
                      {skill}
                      <button onClick={() => handleRemoveSkill(skill)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: 0, fontSize: '16px' }}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginTop: '8px' }}>{skills.length}/10 skills</p>
            </div>

            {/* X Account Verification */}
            <div style={{
              padding: '16px',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
              <label style={labelStyle}>X (Twitter) Account</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(29, 161, 242, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg style={{ width: '20px', height: '20px', color: '#1DA1F2' }} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: 'white' }}>@{user.xUsername}</span>
                    <svg style={{ width: '16px', height: '16px', color: '#14F195' }} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <span style={{ fontSize: '12px', color: '#14F195', fontWeight: 500 }}>Verified</span>
                  </div>
                  <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
                    Your account is verified via X OAuth
                  </p>
                </div>
              </div>
            </div>

            {/* Links */}
            <div>
              <label style={labelStyle}>Links</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg style={{ width: '20px', height: '20px', color: 'rgba(255,255,255,0.6)' }} fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
                  </div>
                  <input type="url" value={github} onChange={(e) => setGithub(e.target.value)} placeholder="https://github.com/username" style={{ ...inputStyle, flex: 1 }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg style={{ width: '20px', height: '20px', color: 'rgba(255,255,255,0.6)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
                  </div>
                  <input type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://yourwebsite.com" style={{ ...inputStyle, flex: 1 }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg style={{ width: '20px', height: '20px', color: 'rgba(255,255,255,0.6)' }} fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
                  </div>
                  <input type="text" value={discord} onChange={(e) => setDiscord(e.target.value)} placeholder="username#0000" style={{ ...inputStyle, flex: 1 }} />
                </div>
              </div>
            </div>
          </div>
          )}

          {activeTab === 'achievements' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'white' }}>Your Achievements</h3>
                <button
                  onClick={() => {
                    setEditingAchievement(null);
                    setAchievementForm({ type: 'hackathon_winner', title: '', description: '', date: '', issuer: '', proofUrl: '', superteamId: '' });
                    setShowAchievementForm(true);
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '10px',
                    background: 'rgba(20, 241, 149, 0.2)',
                    border: '1px solid rgba(20, 241, 149, 0.3)',
                    color: '#14F195',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  + Add Achievement
                </button>
              </div>

              {showAchievementForm && (
                <div style={{ padding: '20px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: 600, color: 'white', marginBottom: '16px' }}>
                    {editingAchievement ? 'Edit Achievement' : 'New Achievement'}
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <label style={labelStyle}>Type *</label>
                      <select
                        value={achievementForm.type}
                        onChange={(e) => setAchievementForm({ ...achievementForm, type: e.target.value as AchievementType })}
                        style={{ ...inputStyle, cursor: 'pointer' }}
                      >
                        <option value="hackathon_winner">Hackathon Winner</option>
                        <option value="grant_winner">Grant Winner</option>
                        <option value="conference_speaker">Conference Speaker</option>
                        <option value="publication">Publication</option>
                        <option value="award">Award</option>
                        <option value="certification">Certification</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Title *</label>
                      <input
                        type="text"
                        value={achievementForm.title}
                        onChange={(e) => setAchievementForm({ ...achievementForm, title: e.target.value })}
                        placeholder="e.g., Solana Hackathon Winner 2024"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Description</label>
                      <textarea
                        value={achievementForm.description}
                        onChange={(e) => setAchievementForm({ ...achievementForm, description: e.target.value })}
                        placeholder="Describe your achievement..."
                        rows={3}
                        style={{ ...inputStyle, resize: 'none' }}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Date</label>
                      <input
                        type="date"
                        value={achievementForm.date}
                        onChange={(e) => setAchievementForm({ ...achievementForm, date: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Issuer</label>
                      <input
                        type="text"
                        value={achievementForm.issuer}
                        onChange={(e) => setAchievementForm({ ...achievementForm, issuer: e.target.value })}
                        placeholder="e.g., Solana Foundation"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Proof URL (optional)</label>
                      <input
                        type="url"
                        value={achievementForm.proofUrl}
                        onChange={(e) => setAchievementForm({ ...achievementForm, proofUrl: e.target.value })}
                        placeholder="https://... (optional)"
                        style={inputStyle}
                      />
                    </div>
                    {achievementForm.type === 'grant_winner' && (
                      <div>
                        <label style={labelStyle}>Superteam</label>
                        <select
                          value={achievementForm.superteamId}
                          onChange={(e) => setAchievementForm({ ...achievementForm, superteamId: e.target.value })}
                          style={{ ...inputStyle, cursor: 'pointer' }}
                        >
                          <option value="">Select a Superteam (optional)</option>
                          {superteams.map((team) => (
                            <option key={team.id} value={team.id}>{team.name} ({team.country})</option>
                          ))}
                        </select>
                        <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                          Select the Superteam this grant came from
                        </p>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={handleSaveAchievement}
                        disabled={saving || !(achievementForm.title && achievementForm.title.trim())}
                        style={{
                          flex: 1,
                          padding: '12px',
                          borderRadius: '10px',
                          background: (achievementForm.title && achievementForm.title.trim()) ? 'linear-gradient(135deg, #14F195, #00C2FF)' : 'rgba(255,255,255,0.1)',
                          border: 'none',
                          color: (achievementForm.title && achievementForm.title.trim()) ? 'black' : 'rgba(255,255,255,0.3)',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: saving || !(achievementForm.title && achievementForm.title.trim()) ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {saving ? 'Saving...' : editingAchievement ? 'Update' : 'Create'}
                      </button>
                      <button
                        onClick={() => {
                          setShowAchievementForm(false);
                          setEditingAchievement(null);
                          setAchievementForm({ type: 'hackathon_winner', title: '', description: '', date: '', issuer: '', proofUrl: '', superteamId: '' });
                        }}
                        style={{
                          padding: '12px 20px',
                          borderRadius: '10px',
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          color: 'rgba(255,255,255,0.7)',
                          fontSize: '14px',
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {loadingAchievements ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>Loading achievements...</div>
              ) : achievements.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
                  No achievements yet. Add your first achievement to showcase your accomplishments!
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {achievements.map(achievement => (
                    <div
                      key={achievement.id}
                      style={{
                        padding: '16px',
                        background: 'rgba(255,255,255,0.03)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.1)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '6px',
                              background: 'rgba(20, 241, 149, 0.1)',
                              color: '#14F195',
                              fontSize: '11px',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                            }}>
                              {achievement.type.replace('_', ' ')}
                            </span>
                            {achievement.type === 'grant_winner' && (
                              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>💰 Grant</span>
                            )}
                          </div>
                          <h4 style={{ fontSize: '16px', fontWeight: 600, color: 'white', marginBottom: '4px' }}>
                            {achievement.title}
                          </h4>
                          {achievement.description && (
                            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>
                              {achievement.description}
                            </p>
                          )}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                            {achievement.issuer && <span>Issued by: {achievement.issuer}</span>}
                            {achievement.superteamId && (
                              <span>
                                Superteam: {superteams.find(st => st.id === achievement.superteamId)?.name || achievement.superteamId}
                              </span>
                            )}
                            {achievement.date && <span>Date: {new Date(achievement.date).toLocaleDateString()}</span>}
                            {achievement.proofUrl && (
                              <a href={achievement.proofUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#14F195', textDecoration: 'none' }}>
                                View Proof →
                              </a>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleEditAchievement(achievement)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '8px',
                              background: 'rgba(255,255,255,0.05)',
                              border: '1px solid rgba(255,255,255,0.1)',
                              color: 'rgba(255,255,255,0.7)',
                              fontSize: '12px',
                              cursor: 'pointer',
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteAchievement(achievement.id)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '8px',
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              color: '#f87171',
                              fontSize: '12px',
                              cursor: 'pointer',
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ flexShrink: 0, padding: '24px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          {activeTab === 'profile' && (
            <button onClick={handleSave} disabled={saving} style={{ width: '100%', padding: '14px', borderRadius: '14px', fontWeight: 600, fontSize: '16px', background: 'linear-gradient(to right, #14F195, #00C2FF)', color: 'black', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: saving ? 0.5 : 1 }}>
              {saving ? (
                <>
                  <div className="animate-spin" style={{ width: '20px', height: '20px', border: '2px solid rgba(0,0,0,0.3)', borderTopColor: 'black', borderRadius: '50%' }} />
                  Saving...
                </>
              ) : 'Save Profile'}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
